/**
 * Responsibility: whether the processor answered recently.
 *
 * Its own file because a module that exports both a hook and a component
 * breaks fast refresh - the same reason `authContext.ts` sits apart from
 * `AuthProvider`.
 *
 * `/healthz` is the probe. It is unauthenticated by design so an uptime
 * monitor needs no credential, which also makes it the cheapest liveness
 * check a browser can make, and it is exempt from the rate limiter so a
 * polling tab cannot throttle itself into reporting an outage.
 */

import { useQuery } from '@tanstack/react-query';

import { hubApi } from '../../api/client';

/** How often to re-probe: quick enough to clear, rare enough to be free. */
const PROBE_SECONDS = 30;

export function useHubOnline(): { online: boolean; checked: boolean } {
  const { isSuccess, isError, isFetched } = useQuery({
    queryKey: ['hub', 'alive'],
    queryFn: () => hubApi.health(),
    refetchInterval: PROBE_SECONDS * 1000,
    refetchOnWindowFocus: true,
    // Tolerant of a single failure on purpose: a banner that flickers on
    // every dropped request trains people to ignore it.
    retry: 1,
    staleTime: 0,
    gcTime: 0,
  });
  return { online: isSuccess || !isError, checked: isFetched };
}
